import styled from "styled-components";
import { useState, useEffect } from "react";
import PrimaryButton from "../../../../../components/FormControls/PrimaryButton";
import SecondaryButton from "../../../../../components/FormControls/SecondaryButton";
import NarrowGrid from "../../../../../components/NarrowGrid";
import FormStringInput from "../../../../../components/FormControls/FormStringInput";
import request from "../../../../../api/request";
import { NotificationManager } from 'react-notifications';

const StyledAccountCategories = styled.div`
    margin-left: 30px;
`;

const WhiteBoard = styled.div`
    background-color: white;
    width: 750px;
    box-sizing: border-box;
    min-height: 460px;
    padding: 30px;
    margin-bottom: 30px;
    color: ${(props) => props.theme.color.gray};
`;

function AccountCategories(props) {

    const [loadedPage, setLoadedPage] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [accountCategories, setAccountCategories] = useState([]);
    const [selectedAccountCategory, setSelectedAccountCategory] = useState(null);
    const [selectedAccountCategoryId, setSelectedAccountCategoryId] = useState(null);
    const [isAddMode, setIsAddMode] = useState(false);
    const [gridColumns, setGridColumns] = useState([
        {id: 'Name', type: 'String', width: 150, textAlign: 'left'},
        {id: 'Code', type: 'String', width: 70, textAlign: 'center'},
    ]);
    const [gridData, setGridData] = useState({groups: [''], items: [[]]});
    const [accountCategoryCode, setAccountCategoryCode] = useState('');
    const [accountCategoryName, setAccountCategoryName] = useState('');
    const [needsValidation, setNeedsValidation] = useState(false);
    const [isAddingDuplicatedItem, setIsAddingDuplicatedItem] = useState(false);

    const onSelectGridItem = (item) => {
        setSelectedAccountCategoryId(item.Id);
        setSelectedAccountCategory(item);
    }
    
    const fetchAccountCategories = async (accountCategoryCodeToSelect) => {
        try {
            let itemList = [];

            const resp = (await request('database', 'fetchAccountCategories'));
            if (resp.ok) {
                resp.data.map((item, index) => {
                    itemList.push(item);
                });
                setAccountCategories(itemList);
                rebuildGridData(itemList, accountCategoryCodeToSelect);
            } else {
                console.log(resp.data);
                NotificationManager.error(resp.data);
                return false;
            }
        } catch (e) {
            console.log('Failed to fetch account categories: ', e);
            return false;
        }
        return true;
    }

    const onDeleteAccountCategory = async () => {
        if (selectedAccountCategory) {
            setIsDeleting(true);
            let deleteData = {};
            deleteData.AccountCategoryCode = selectedAccountCategory.AccountCategoryCode;

            const resp = (await request('database', 'deleteAccountCategory', [deleteData]));
            if (resp.ok) {
                setSelectedAccountCategory(null);
                setSelectedAccountCategoryId(null);
                fetchAccountCategories();
                NotificationManager.success('Successfully deleted account category');
            } else
            {
                console.log(resp.data);
                NotificationManager.error(resp.data);
            }
            setIsDeleting(false);
        }
    }

    const onSaveChanges = async () => {
        if (isAddMode) { // adding new item
            if (accountCategoryName === '' || accountCategoryCode === '') {
                setNeedsValidation(true);
            } else {
                // check duplicated code
                if (gridData.items[0].length > 0 && (gridData.items[0].find((itm) => itm.AccountCategoryCode.replace(/\s/g, '') === accountCategoryCode.replace(/\s/g, '')))) {
                    setIsAddingDuplicatedItem(true);
                } else {
                    setIsSaving(true);
                    let saveData = {};
                    saveData.AccountCategoryCode = accountCategoryCode;
                    saveData.AccountCategoryName = accountCategoryName;

                    const resp = (await request('database', 'createAccountCategory', [saveData]));
                    if (resp.ok) {
                        fetchAccountCategories(accountCategoryCode);
                        NotificationManager.success('Successfully added account category');
                    } else
                    {
                        console.log(resp.data);
                        NotificationManager.error(resp.data);
                    }
                    setIsSaving(false);
                    setIsAddMode(false);
                }
            }
        } else {
            if (selectedAccountCategory && accountCategoryCode === '') {
                setNeedsValidation(true);
            } else {
                setIsSaving(true);
                let saveData = {};
                saveData.AccountCategoryCode = accountCategoryCode;
                saveData.AccountCategoryName = accountCategoryName;

                const resp = (await request('database', 'updateAccountCategory', [saveData]));
                if (resp.ok) {
                    fetchAccountCategories(accountCategoryCode);
                    NotificationManager.success('Successfully updated account category');
                } else
                {
                    console.log(resp.data);
                    NotificationManager.error(resp.data);
                }
                setIsSaving(false);
            }
        }
    }

    const rebuildGridData = (items, accountCategoryCodeToSelect) => {
        if (items && items.length > 0) {
            let itemList = [];
            items.map((item, index) => {
                let gridItem = {Id: index, Name: item.AccountCategoryName, Code: item.AccountCategoryCode, ...item};
                itemList.push(gridItem);
                if (accountCategoryCodeToSelect && item.AccountCategoryCode === accountCategoryCodeToSelect) {
                    setSelectedAccountCategoryId(index);
                    setSelectedAccountCategory(gridItem);
                }
            });
            setGridData({groups: [''], items: [itemList]});
        }
    }

    useEffect(() => {
        if (!loadedPage) {
            props.setPageTitle('Account categories');
            fetchAccountCategories();
            setLoadedPage(true);
        }
    }, []);

    useEffect(() => {
        if (isAddMode) {
            setNeedsValidation(false);
            setAccountCategoryName('');
            setAccountCategoryCode('');
        } else if (selectedAccountCategory) {
            setNeedsValidation(false);
            setAccountCategoryName(selectedAccountCategory.AccountCategoryName);
            setAccountCategoryCode(selectedAccountCategory.AccountCategoryCode);
        }
    }, [isAddMode]);

    useEffect(() => {
        if (selectedAccountCategory) {
            setNeedsValidation(false);
            setAccountCategoryName(selectedAccountCategory.AccountCategoryName);
            setAccountCategoryCode(selectedAccountCategory.AccountCategoryCode);
        } else {
            setNeedsValidation(false);
            setAccountCategoryName('');
            setAccountCategoryCode('');
        }
    }, [selectedAccountCategory]);

    useEffect(() => {
        setIsAddingDuplicatedItem(false);
        setNeedsValidation(false);
    }, [selectedAccountCategory, isAddMode]);

    return (
        <StyledAccountCategories>
            <WhiteBoard>
                <div style={{display:'flex'}}>
                    <div>
                        <NarrowGrid id='generaltables_accountcategories_grid' gridColumns={gridColumns} gridData={gridData} disabled={isAddMode} itemIdToSelect={selectedAccountCategoryId} onSelectGridItem={onSelectGridItem} gridStyle={{width:220, height:350, marginTop:20}}></NarrowGrid>
                        <div style={{marginTop:20}}>
                            {selectedAccountCategoryId !== null && !isAddMode && <SecondaryButton caption='Delete' isLoading={isDeleting} onClick={() => onDeleteAccountCategory()} style={{width:75, height:25}}></SecondaryButton>}
                            {isAddMode && <SecondaryButton caption='Cancel' onClick={() => setIsAddMode(false)} style={{width:75, height:25, float:'right'}}></SecondaryButton>}
                            {!isAddMode && <PrimaryButton caption='Create new' onClick={() => setIsAddMode(true)} style={{width:100, height:25, float:'right'}}></PrimaryButton>}
                        </div>
                    </div>
                    <div style={{marginLeft:30}}>
                        <FormStringInput label='Code' value={accountCategoryCode} onChange={(val) => setAccountCategoryCode(val)} inputStyle={{width:100, height:35}} validated={needsValidation && accountCategoryCode === '' ? false : undefined} disabled={!isAddMode} ></FormStringInput>
                        <FormStringInput label='Name' value={accountCategoryName} onChange={(val) => setAccountCategoryName(val)} inputStyle={{width:290, height:35}} validated={needsValidation && accountCategoryName === '' ? false : undefined} disabled={!isAddMode && !selectedAccountCategory}></FormStringInput>
                    {isAddingDuplicatedItem && <div style={{marginTop:15, width:280, fontSize:12, fontWeight:700, color:'#dc3545'}}>* Code is duplicated.</div>}
                    </div>
                </div>
            </WhiteBoard>
            <div>
                <PrimaryButton caption={isAddMode ? 'Add account category' : 'Save changes'} isLoading={isSaving} onClick={() => onSaveChanges()} disabled={isAddMode || selectedAccountCategory ? false : true} style={{width:150, float:'right'}}></PrimaryButton>
            </div>
        </StyledAccountCategories>
    )
}

export default AccountCategories;